import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Leaf, Heart, Brain, Users, Wallet, Sparkles, Globe,
  BookOpen, Notebook, Play, Mic, Newspaper, MessageCircle, Quote,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import BrandHeader from "@/components/BrandHeader";
import BrandFooter from "@/components/BrandFooter";
import EmailSignup from "@/components/EmailSignup";

const IMG_HERO = "https://images.unsplash.com/photo-1499750310107-5fef28a66643?auto=format&fit=crop&w=1800&q=80";
const IMG_JOURNAL = "https://images.unsplash.com/photo-1517842645767-c639042777db?auto=format&fit=crop&w=1400&q=80";
const IMG_ABOUT = "https://images.unsplash.com/photo-1490730141103-6cac27aaab94?auto=format&fit=crop&w=1400&q=80";
const IMG_SIGNUP = "https://images.unsplash.com/photo-1518156677180-95a2893f3e9f?auto=format&fit=crop&w=1400&q=80";

const EASE = [0.16, 1, 0.3, 1];

const pillars = [
  { Icon: Leaf, title: "Physical Wellness", body: "Move your body. Fuel yourself well. Sleep deeply." },
  { Icon: Heart, title: "Emotional Wellness", body: "Build resilience. Manage emotions. Find peace." },
  { Icon: Brain, title: "Mental Wellness", body: "Develop discipline. Strengthen your mindset. Learn continuously." },
  { Icon: Users, title: "Social Wellness", body: "Strengthen relationships. Build meaningful connections." },
  { Icon: Wallet, title: "Financial Wellness", body: "Reduce stress. Increase confidence. Build financial freedom." },
  { Icon: Sparkles, title: "Spiritual Wellness", body: "Purpose. Faith. Reflection. Gratitude." },
  { Icon: Globe, title: "Environmental Wellness", body: "Create spaces that help you thrive." },
];

const resources = [
  { Icon: BookOpen, title: "Free Guides", slug: "guides", body: "Practical, printable, and free — start today." },
  { Icon: Notebook, title: "Journals", slug: "journals", body: "7, 30, and 90-day discipline journals." },
  { Icon: Play, title: "Videos", slug: "videos", body: "Short lessons on habits, mindset, and health." },
  { Icon: Mic, title: "Podcast", slug: "podcast", body: "Coming soon — real conversations on wellness." },
  { Icon: Newspaper, title: "Articles", slug: "articles", body: "Written encouragement, weekly." },
  { Icon: MessageCircle, title: "Community", slug: "community", body: "A quiet, kind space to grow together." },
];

const testimonials = [
  {
    name: "Marcus H.",
    role: "34 · Charlotte, NC",
    body: "The 7-day challenge was the first thing I've finished in years. Not because it was easy — because it was small enough that I couldn't lie to myself about it.",
  },
  {
    name: "Danielle R.",
    role: "41 · Denver, CO",
    body: "I stopped waiting to feel motivated. The journal made showing up boring, and boring is where the real change happened.",
  },
  {
    name: "Kevin O.",
    role: "29 · Brooklyn, NY",
    body: "I was skeptical about another wellness thing. This one didn't try to sell me a new identity — it helped me keep the one I already wanted.",
  },
];

const Hero = () => (
  <section className="relative overflow-hidden bg-[#F8F6F2]">
    <div className="max-w-7xl mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-20 md:pb-28 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
      <div className="lg:col-span-6">
        <motion.p
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}
          className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.28em] uppercase text-[#8FAF8A] mb-6"
          data-testid="hero-overline"
        >
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#F6C453]" />
          Whole-Person Wellness
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05, ease: EASE }}
          className="font-serif-display text-[2.6rem] sm:text-5xl lg:text-[3.75rem] leading-[1.05] tracking-tight text-[#333333] font-medium"
          data-testid="hero-headline"
        >
          Wellness Isn&apos;t a Destination.
          <br />
          <span className="italic text-[#6E8F6A]">It&apos;s the Way You Live Every Day.</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
          className="mt-7 text-lg md:text-xl text-[#4A4A4A] leading-relaxed max-w-xl"
          data-testid="hero-subheadline"
        >
          Helping you create a healthier life through small daily choices that strengthen your mind, body, relationships, finances, purpose, and spirit.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.25, ease: EASE }}
          className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        >
          <Button
            asChild
            className="group rounded-full bg-[#333333] hover:bg-black text-white h-14 px-8 text-base font-semibold shadow-lg shadow-black/10 transition-all hover:-translate-y-0.5"
            data-testid="hero-primary-cta"
          >
            <a href="#join">Start Your Wellness Journey <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" /></a>
          </Button>
          <Button
            asChild variant="outline"
            className="rounded-full bg-white border-[#EDE9E1] text-[#333333] hover:bg-[#F8F6F2] h-14 px-8 text-base font-semibold"
            data-testid="hero-secondary-cta"
          >
            <Link to="/resources">Explore Resources</Link>
          </Button>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
        className="lg:col-span-6 relative"
        data-testid="hero-visual"
      >
        <div className="relative rounded-[2.25rem] overflow-hidden shadow-[0_40px_100px_-30px_rgba(51,51,51,0.28)]">
          <img src={IMG_HERO} alt="Morning sunrise with coffee and journal" className="w-full h-[440px] md:h-[620px] object-cover" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-tr from-black/10 via-transparent to-transparent" />
        </div>
        <div className="hidden md:flex absolute -bottom-6 -left-6 items-center gap-3 bg-white/95 backdrop-blur rounded-2xl shadow-xl border border-[#EDE9E1] px-5 py-4 max-w-xs">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#F6C453]/25">
            <Sparkles className="h-5 w-5 text-[#B8860B]" />
          </span>
          <div>
            <div className="font-display font-bold text-sm text-[#333333]">Small choices, daily.</div>
            <div className="text-xs text-[#8A8A8A]">Progress over perfection.</div>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

const Featured = () => (
  <section className="py-24 md:py-32 bg-white" data-testid="featured-program-section">
    <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-14 items-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.8, ease: EASE }}
        className="lg:col-span-6 relative"
      >
        <div className="rounded-[2rem] overflow-hidden shadow-[0_30px_80px_-30px_rgba(51,51,51,0.2)]">
          <img src={IMG_JOURNAL} alt="Wellness journals on a soft-lit desk" className="w-full h-[420px] md:h-[560px] object-cover" loading="lazy" />
        </div>
        <div className="absolute -top-6 -right-6 hidden md:block bg-[#333333] text-white rounded-2xl shadow-xl px-5 py-4">
          <div className="text-[10px] tracking-[0.25em] uppercase opacity-80 font-bold">Flagship Program</div>
          <div className="font-display font-bold text-lg">The Strong Force Daily</div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7, ease: EASE }}
        className="lg:col-span-6"
      >
        <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#8FAF8A] mb-4">Begin Here</p>
        <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-[3.25rem] font-medium tracking-tight text-[#333333] leading-[1.08]">
          Begin with <span className="italic">The Strong Force Daily.</span>
        </h2>
        <p className="mt-6 text-lg text-[#4A4A4A] leading-relaxed max-w-lg">
          Build the habits that make every other area of wellness possible. Start with the free challenge, then grow into the 30 and 90-day journals.
        </p>
        <ul className="mt-8 space-y-3 text-[#333333]">
          {[
            "Free 7-Day Discipline Challenge",
            "30-Day Discipline Journal",
            "90-Day Discipline Journal",
          ].map((t, i) => (
            <li key={t} className="flex items-center gap-3" data-testid={`featured-bullet-${i + 1}`}>
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#F6C453]/30 text-[#B8860B] text-xs font-bold">{i + 1}</span>
              <span className="font-medium">{t}</span>
            </li>
          ))}
        </ul>
        <div className="mt-9 flex flex-col sm:flex-row gap-4">
          <Button
            asChild
            className="rounded-full bg-[#333333] hover:bg-black text-white h-13 px-7 font-semibold shadow-lg shadow-black/10 transition-all hover:-translate-y-0.5"
            data-testid="featured-primary-cta"
          >
            <Link to="/the-strong-force-daily">Start Free <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
          <Button
            asChild variant="ghost"
            className="rounded-full h-13 px-6 text-[#333333] hover:bg-[#F8F6F2]"
            data-testid="featured-secondary-cta"
          >
            <Link to="/shop">Explore Journals →</Link>
          </Button>
        </div>
      </motion.div>
    </div>
  </section>
);

const Pillars = () => (
  <section className="py-24 md:py-32 bg-[#F8F6F2]" data-testid="pillars-section">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <div className="max-w-3xl">
        <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#8FAF8A] mb-4">The Seven Pillars</p>
        <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-[#333333] leading-[1.05]">
          Whole-Person <span className="italic">Wellness.</span>
        </h2>
        <p className="mt-5 text-lg text-[#4A4A4A] max-w-xl">
          Real transformation happens when every area of your life gets a little stronger — a little at a time.
        </p>
      </div>

      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
        {pillars.map(({ Icon, title, body }, i) => (
          <motion.div
            key={title}
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6, delay: (i % 3) * 0.06, ease: EASE }}
            className="group bg-white rounded-3xl p-8 border border-[#EDE9E1] hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
            data-testid={`pillar-card-${i + 1}`}
          >
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#8FAF8A]/12 text-[#6E8F6A] group-hover:bg-[#8FAF8A] group-hover:text-white transition-colors">
              <Icon className="h-5.5 w-5.5" strokeWidth={1.75} />
            </span>
            <h3 className="mt-6 font-display text-xl md:text-2xl font-bold text-[#333333]">{title}</h3>
            <p className="mt-3 text-[#4A4A4A] leading-relaxed">{body}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const About = () => (
  <section className="py-24 md:py-32 bg-white" data-testid="about-section">
    <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
      <motion.div
        initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7, ease: EASE }}
      >
        <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#8FAF8A] mb-4">Our Mission</p>
        <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-[3.25rem] font-medium tracking-tight text-[#333333] leading-[1.08]">
          Progress, <span className="italic">not perfection.</span>
        </h2>
        <div className="mt-7 space-y-5 text-lg text-[#4A4A4A] leading-relaxed max-w-lg">
          <p>At Wishing Wellness, we believe real transformation doesn&apos;t happen overnight.</p>
          <p>It happens through small choices made consistently.</p>
          <p>We&apos;re here to encourage, educate, and equip people to build healthier lives from the inside out.</p>
          <p className="text-[#333333] font-semibold">No perfection. No unrealistic expectations. Just progress. Every single day.</p>
        </div>
        <div className="mt-8">
          <Button
            asChild variant="outline"
            className="rounded-full bg-white border-[#EDE9E1] text-[#333333] hover:bg-[#F8F6F2] h-12 px-6 font-semibold"
            data-testid="about-cta"
          >
            <Link to="/about">Read Our Story →</Link>
          </Button>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.97 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.9, ease: EASE }}
        className="relative"
      >
        <div className="rounded-[2rem] overflow-hidden shadow-[0_30px_80px_-30px_rgba(51,51,51,0.2)]">
          <img src={IMG_ABOUT} alt="Sunlight through green leaves" className="w-full h-[440px] md:h-[560px] object-cover" loading="lazy" />
        </div>
      </motion.div>
    </div>
  </section>
);

const Resources = () => (
  <section className="py-24 md:py-32 bg-[#F8F6F2]" data-testid="resources-section">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
        <div className="max-w-2xl">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#8FAF8A] mb-4">Resources</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl font-medium tracking-tight text-[#333333] leading-[1.08]">
            Tools for the <span className="italic">quiet work</span> of getting better.
          </h2>
        </div>
        <Link
          to="/resources"
          className="inline-flex items-center gap-2 text-[#333333] font-semibold hover:text-[#6E8F6A] transition-colors"
          data-testid="resources-view-all"
        >
          View all resources <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
        {resources.map(({ Icon, title, slug, body }, i) => (
          <motion.div
            key={slug}
            initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.55, delay: (i % 3) * 0.05, ease: EASE }}
          >
            <Link
              to={`/resources/${slug}`}
              className="group block bg-white rounded-3xl p-8 border border-[#EDE9E1] hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              data-testid={`resource-card-${slug}`}
            >
              <div className="flex items-start justify-between">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#F6C453]/25 text-[#B8860B] group-hover:bg-[#F6C453] group-hover:text-white transition-colors">
                  <Icon className="h-5.5 w-5.5" strokeWidth={1.75} />
                </span>
                <ArrowRight className="h-4 w-4 text-[#8A8A8A] group-hover:text-[#333333] transition-transform group-hover:translate-x-1" />
              </div>
              <h3 className="mt-6 font-display text-xl md:text-2xl font-bold text-[#333333]">{title}</h3>
              <p className="mt-2 text-[#4A4A4A]">{body}</p>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const JoinCommunity = () => (
  <section id="join" className="py-24 md:py-32 bg-white" data-testid="join-section">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <div className="bg-[#F8F6F2] rounded-[2rem] md:rounded-[3rem] overflow-hidden border border-[#EDE9E1] grid lg:grid-cols-2">
        <div className="p-8 sm:p-12 md:p-16 lg:p-20 flex flex-col justify-center">
          <EmailSignup source="wishing_wellness" testIdPrefix="join" />
        </div>
        <div className="relative min-h-[320px] lg:min-h-full">
          <img src={IMG_SIGNUP} alt="Quiet outdoor walk at golden hour" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-tr from-[#333333]/45 via-[#333333]/10 to-transparent" />
          <div className="absolute bottom-8 left-8 right-8 text-white font-serif-display">
            <p className="text-xs tracking-[0.3em] uppercase opacity-90 font-bold">Weekly, thoughtful</p>
            <p className="mt-2 text-2xl md:text-3xl leading-tight max-w-sm">
              One quiet email. Real encouragement.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Testimonials = () => (
  <section className="py-24 md:py-32 bg-[#F8F6F2]" data-testid="testimonials-section">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <div className="max-w-3xl">
        <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#8FAF8A] mb-4">Real Stories</p>
        <h2 className="font-serif-display text-4xl sm:text-5xl font-medium tracking-tight text-[#333333] leading-[1.08]">
          Real stories. <span className="italic">Real progress.</span>
        </h2>
      </div>
      <div className="mt-12 grid md:grid-cols-3 gap-5 md:gap-6">
        {testimonials.map((t, i) => (
          <div
            key={i}
            className="bg-white rounded-3xl p-8 border border-[#EDE9E1] flex flex-col"
            data-testid={`testimonial-${i + 1}`}
          >
            <Quote className="h-6 w-6 text-[#F6C453]" />
            <p className="mt-4 text-[#4A4A4A] leading-relaxed italic">&ldquo;{t.body}&rdquo;</p>
            <div className="mt-6 pt-6 border-t border-[#EDE9E1]">
              <div className="font-display font-bold text-[#333333]">{t.name}</div>
              <div className="text-sm text-[#8A8A8A]">{t.role}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <div className="min-h-screen bg-white text-[#333333]" data-testid="home-page">
      <BrandHeader variant="cream" />
      <main>
        <Hero />
        <Featured />
        <Pillars />
        <About />
        <Resources />
        <JoinCommunity />
        <Testimonials />
      </main>
      <BrandFooter />
    </div>
  );
}
